import { useState } from "react";
import { usePagedPortalItems } from "../hooks/usePagedPortalItems";
import type { PortalArtifactDto, PortalWorkMapDto } from "../model";
import { PrototypeViewer } from "./PrototypeViewer";

function formatSize(bytes: number) {
	if (bytes < 1024) return `${bytes} B`;
	if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(bytes < 10 * 1024 ? 1 : 0)} KB`;
	return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatReportDate(value: string) {
	return new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" }).format(new Date(value));
}

export function isHtmlPrototype(item: Pick<PortalArtifactDto, "artifactType" | "mimeType">) {
	return item.artifactType === "prototype" && item.mimeType === "text/html";
}

export function prototypeTitle(item: Pick<PortalArtifactDto, "name" | "prototypeContext">) {
	if (item.prototypeContext?.title?.trim()) return item.prototypeContext.title.trim();
	const filename = item.name.split("/").at(-1) ?? item.name;
	const stem = filename
		.replace(/\.html?$/i, "")
		.replace(/-[0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12}$/i, "")
		.replace(
			/-([a-z])-([a-z])-([a-z])$/i,
			(_, first: string, second: string, third: string) =>
				` ${first.toUpperCase()}/${second.toUpperCase()}/${third.toUpperCase()}`,
		)
		.replace(/[-_]+/g, " ")
		.trim();
	if (stem) return `${stem.charAt(0).toUpperCase()}${stem.slice(1)}`;
	return item.prototypeContext?.ticketTitle ?? "Interactive prototype";
}

export function prototypeDescription(item: Pick<PortalArtifactDto, "prototypeContext">) {
	const context = item.prototypeContext;
	if (context?.question?.trim()) return `Created to answer: ${context.question.trim()}`;
	if (context?.description?.trim()) return context.description.trim();
	if (context?.ticketTitle?.trim()) return `Created to explore ${context.ticketTitle.trim()}.`;
	return "Created to make a discovery decision tangible before implementation.";
}

export function partitionArtifacts<
	T extends Pick<PortalArtifactDto, "artifactType" | "deliveryReport" | "isDeliverable" | "mimeType">,
>(items: readonly T[]) {
	return {
		reports: items.filter((item) => item.deliveryReport),
		deliverables: items.filter((item) => item.isDeliverable && !item.deliveryReport && !isHtmlPrototype(item)),
		supporting: items.filter((item) => !item.deliveryReport && (!item.isDeliverable || isHtmlPrototype(item))),
	};
}

export function ArtifactView({
	projectId,
	projectName,
	map,
}: {
	projectId: string;
	projectName: string;
	map: Pick<PortalWorkMapDto, "id" | "title">;
}) {
	const [selectedReport, setSelectedReport] = useState<PortalArtifactDto>();
	const [selectedSupporting, setSelectedSupporting] = useState<PortalArtifactDto>();
	const [downloading, setDownloading] = useState<string>();
	const [downloadError, setDownloadError] = useState<string>();
	const [showAllProjectFiles, setShowAllProjectFiles] = useState(false);
	const artifactPath = `/api/portal/v1/projects/${projectId}/artifacts`;
	const { items, loading, loadingMore, failed, hasMore, loadMore } = usePagedPortalItems<PortalArtifactDto>(
		showAllProjectFiles ? artifactPath : `${artifactPath}?mapId=${encodeURIComponent(map.id)}`,
	);
	const { reports, deliverables, supporting } = partitionArtifacts(items);

	async function open(item: PortalArtifactDto, select = setSelectedSupporting) {
		setDownloadError(undefined);
		setDownloading(item.id);
		try {
			if (isHtmlPrototype(item)) throw new Error("Open this prototype in its rendered viewer.");
			const response = await fetch(`/api/portal/v1/projects/${projectId}/artifacts/${item.id}`, {
				credentials: "include",
			});
			if (!response.ok) throw new Error("This file could not be opened.");
			if (response.headers.get("Content-Type")?.includes("application/json")) {
				const envelope = (await response.json()) as { data: PortalArtifactDto };
				select(envelope.data);
				return;
			}
			const url = URL.createObjectURL(await response.blob());
			const anchor = document.createElement("a");
			anchor.href = url;
			anchor.download = item.downloadName ?? item.name.split("/").at(-1) ?? item.name;
			anchor.click();
			URL.revokeObjectURL(url);
		} catch (error) {
			setDownloadError(error instanceof Error ? error.message : "This file could not be opened.");
		} finally {
			setDownloading(undefined);
		}
	}

	function toggleArtifactScope() {
		setSelectedReport(undefined);
		setSelectedSupporting(undefined);
		setShowAllProjectFiles((current) => !current);
	}

	return (
		<section className="project-files" aria-labelledby="artifacts-heading">
			<header className="project-files__header">
				<div>
					<p className="eyebrow">Project handoff</p>
					<h2 id="artifacts-heading">Files you can use</h2>
				</div>
				<p>
					Review project files or download finished builds. Prior delivery reports remain available below. For a
					running web build, use Preview instead.
				</p>
			</header>
			<div className="artifact-scope" role="group" aria-label="Artifact scope">
				<div>
					<strong>{showAllProjectFiles ? projectName : map.title}</strong>
					<span>
						{showAllProjectFiles
							? "All files shared with this project"
							: `Current discovery map · ${projectName}`}
					</span>
				</div>
				<button type="button" className="secondary" onClick={toggleArtifactScope}>
					{showAllProjectFiles ? "Use current map" : "Show all project files"}
				</button>
			</div>
			{loading && <p role="status">Loading project files…</p>}
			{failed && <p role="alert">Project files could not be loaded.</p>}
			{downloadError && (
				<p role="alert" className="form-error">
					{downloadError}
				</p>
			)}

			{!loading && !failed && (
				<>
					<section className="build-products" aria-labelledby="build-products-heading">
						<div className="section-heading-row">
							<div>
								<p className="eyebrow">Ready to install or run</p>
								<h3 id="build-products-heading">Build products</h3>
							</div>
							<span className="file-count">{deliverables.length}</span>
						</div>
						{deliverables.length === 0 ? (
							<p className="compact-empty-state">
								{showAllProjectFiles
									? "No downloadable build has been published yet. The next successful delivery iteration will inspect its build outputs automatically."
									: "No downloadable build is linked to this discovery map yet."}
							</p>
						) : (
							<div className="delivery-grid">
								{deliverables.map((item) => (
									<article className="delivery-card" key={item.id}>
										<div className="delivery-card__meta">
											{item.platform && <span>{item.platform}</span>}
											{item.kind && <span>{item.kind.replace(/([A-Z])/g, " $1")}</span>}
										</div>
										<h4>{item.downloadName ?? item.name}</h4>
										<p>
											{item.description ?? "A finished build produced and verified by a project iteration."}
										</p>
										<div className="delivery-card__footer">
											<small>{formatSize(item.fileSize)}</small>
											<button
												type="button"
												onClick={() => void open(item)}
												disabled={downloading === item.id}
											>
												{downloading === item.id ? "Preparing…" : "Download"}
											</button>
										</div>
									</article>
								))}
							</div>
						)}
					</section>

					<section className="supporting-files" aria-labelledby="supporting-files-heading">
						<div className="section-heading-row">
							<div>
								<p className="eyebrow">Project record</p>
								<h3 id="supporting-files-heading">Supporting files</h3>
							</div>
							<span className="file-count">{supporting.length}</span>
						</div>
						{supporting.length === 0 ? (
							<p className="compact-empty-state">
								{showAllProjectFiles
									? "No supporting files have been shared."
									: "No supporting files are linked to this discovery map."}
							</p>
						) : (
							<div className="split-list">
								<ul>
									{supporting.map((item) => (
										<li key={item.id}>
											{isHtmlPrototype(item) ? (
												<div className="supporting-prototype">
													<div className="supporting-prototype__copy">
														<h4>{prototypeTitle(item)}</h4>
														<p>{prototypeDescription(item)}</p>
														<dl className="prototype-provenance">
															<div>
																<dt>Project</dt>
																<dd>{projectName}</dd>
															</div>
															<div>
																<dt>Discovery phase</dt>
																<dd>{item.prototypeContext?.mapTitle ?? "Not recorded"}</dd>
															</div>
														</dl>
														<small>{formatSize(item.fileSize)} · Interactive prototype</small>
													</div>
													<PrototypeViewer
														projectId={projectId}
														artifact={{ artifactId: item.id, name: prototypeTitle(item) }}
													/>
												</div>
											) : (
												<button className="text-button" type="button" onClick={() => void open(item)}>
													{item.name}
													<small>{formatSize(item.fileSize)}</small>
												</button>
											)}
										</li>
									))}
								</ul>
								<article>
									{selectedSupporting ? (
										<>
											<h4>{selectedSupporting.name}</h4>
											<pre>{selectedSupporting.content ?? "Binary file"}</pre>
										</>
									) : (
										<p>Choose a file to read it here.</p>
									)}
								</article>
							</div>
						)}
					</section>

					<section className="delivery-reports" aria-labelledby="delivery-reports-heading">
						<div className="section-heading-row">
							<div>
								<p className="eyebrow">Context for the next iteration</p>
								<h3 id="delivery-reports-heading">Delivery reports</h3>
							</div>
							<span className="file-count">{reports.length}</span>
						</div>
						<p className="section-intro">
							{showAllProjectFiles
								? "These reports come from every delivery map in this project. The Project agent can read them when planning follow-up work."
								: "These reports and outputs trace back to the current discovery map and its Spec."}
						</p>
						{reports.length === 0 ? (
							<p className="compact-empty-state">
								{showAllProjectFiles
									? "No delivery reports are available yet. Completed delivery iterations will publish them here."
									: "No delivery reports trace back to this discovery map yet."}
							</p>
						) : (
							<div className="split-list">
								<ul>
									{reports.map((item) => (
										<li key={item.id}>
											<button
												className="text-button report-list-button"
												type="button"
												onClick={() => void open(item, setSelectedReport)}
											>
												{item.deliveryReport?.workTicketTitle ?? item.name}
												{item.deliveryReport && (
													<small>
														{item.deliveryReport.sourceMapTitle} · {item.deliveryReport.outcome} ·{" "}
														{formatReportDate(item.deliveryReport.completedAt)}
													</small>
												)}
											</button>
										</li>
									))}
								</ul>
								<article>
									{selectedReport?.deliveryReport ? (
										<>
											<h4>{selectedReport.deliveryReport.workTicketTitle}</h4>
											<p className="report-provenance">
												From <strong>{selectedReport.deliveryReport.sourceMapTitle}</strong> ·{" "}
												{selectedReport.deliveryReport.outcome} ·{" "}
												{formatReportDate(selectedReport.deliveryReport.completedAt)}
											</p>
											<p>{selectedReport.deliveryReport.summary}</p>
											<pre>{selectedReport.content ?? "Report content is unavailable."}</pre>
										</>
									) : (
										<p>Choose a report to read its outcome and full delivery context.</p>
									)}
								</article>
							</div>
						)}
					</section>
				</>
			)}
			{hasMore && (
				<button type="button" className="secondary" disabled={loadingMore} onClick={() => void loadMore()}>
					{loadingMore ? "Loading…" : "Load more files"}
				</button>
			)}
		</section>
	);
}
